import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  inject
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { CoreActionSetFormCurrentPageStatus } from '@bepatient/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import {
  ConstraintTypesV3,
  QuestionsV3,
  QuestionV3ControlTypes
} from '../../classes/enum';
import { QuestionBaseV3 } from '../../classes/question-base';
// import { FieldsBlocQuestionV3 } from '../../classes/question-fieldsBloc';
import { FormV3InvalidField } from '../../models/dynamic-form-v3.models';
import { DynamicFormV3UtilsService } from '../../services/dynamic-form-v3-utils.service';
import { DynamicFormQuestionParserServiceV3 } from '../../services/question-parser.service';
import { emailValidator } from '../../validators/dynamic-form-email.validator';
import { lengthValidator } from '../../validators/dynamic-form-length.validator';
import { LimitCondition } from '../../validators/dynamic-form-limitCondition.validator';
import {
  maxSizeValidator,
  allowedExtensionValidator
} from '../../validators/dynamic-form-photo.validator';
import { createRangeValidator } from '../../validators/dynamic-form-range.validator';
import { RequiredGroupValidator } from '../../validators/dynamic-form-requiredGroup.validator';

interface Page {
  index: number;
  skipPage: boolean;
  questions: QuestionsV3[];
  formGroup: UntypedFormGroup;
  formSubscriptions: Array<Subscription>;
}

interface Pending {
  responses: any;
  currentPage: number;
  prevPage: number;
  questionPages: QuestionsV3[][];
  invalidFields: FormV3InvalidField[];
  dataToCheckFields: string[];
}

@Component({
  selector: 'bepatient-dynamic-form-v3',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
  standalone: false
})
export class DynamicFormComponentV3 implements OnDestroy {
  private dynamicForm = inject(DynamicFormV3UtilsService);
  private formBuilder = inject(UntypedFormBuilder);
  private parser = inject(DynamicFormQuestionParserServiceV3);
  private store$ = inject<Store<any>>(Store);

  @Input() readonly: boolean;
  @Input() isLoadingForm: boolean;

  @Output() pageValid = new EventEmitter<boolean>();
  @Output() pageUpdated = new EventEmitter<boolean>();
  @Output() colorChange = new EventEmitter<string>();
  @Output() progressChange = new EventEmitter<[number, number]>();
  @Output() responsesChange = new EventEmitter<any>();
  @Output() invalidFieldIDS = new EventEmitter<string[]>();
  @Output() skipPageChange = new EventEmitter<string[]>();

  pages: Array<Page>;

  // mainFormGroup added to keep all responses available in the object
  // individuals formGroups are still being used for validPage
  mainFormGroup: UntypedFormGroup;
  mainFormSubscriptions: Array<Subscription>;

  private _color = '';
  private _currentPage = 0;
  private _progress: [number, number] = [0, 0];

  private pending: Pending = {
    responses: void 0,
    currentPage: void 0,
    prevPage: void 0,
    questionPages: void 0,
    invalidFields: void 0,
    dataToCheckFields: void 0
  };

  private pageValidation: boolean;

  @Input() set color(v: string) {
    this._color = v;
    this.colorChange.emit(this.color);
  }

  @Input() set responses(v: any) {
    this.pending.responses = v;
    this.processPending();
  }

  @Input() set questionPages(v: QuestionsV3[][]) {
    this.pending.questionPages = v;
    this.processPending();
  }

  @Input() set invalidFields(v: FormV3InvalidField[]) {
    this.pending.invalidFields = v;
    this.processPending();
  }

  @Input() set dataToCheckFields(v: string[]) {
    this.pending.dataToCheckFields = v;
    this.processPending();
  }

  @Input() set prevPage(p: number) {
    this.pending.prevPage = p;
    this.processPending();
  }

  @Input() skipPage: boolean[];

  @Input() set currentPage(v: number) {
    this.dynamicForm.updatePage$.subscribe(n => {
      this.pending.currentPage = n;
      this.processPending();
    });
    this.pending.currentPage = v;
    this.processPending();
  }

  get color(): string {
    return this._color;
  }

  get currentPage(): number {
    return this._currentPage;
  }

  get progress(): [number, number] {
    return this._progress;
  }

  ngOnDestroy(): void {
    this.pageFormUnsubscribe();
  }

  private processPending(): void {
    if (this.pending.questionPages) {
      this.pageFormUnsubscribe();
      /**
       * Create a overall form group consisting all pages
       */
      this.mainFormGroup = new UntypedFormGroup({});

      this.pending.questionPages.forEach((page, index) => {
        this.mainFormGroup.addControl(
          index.toString(), // page index (page number) as the FormGroup Name
          this.createFormGroup(page) // create FormGroup with FormControls for each question inside a page
        );
      });

      this.mainFormSubscriptions = [
        this.mainFormGroup.valueChanges.subscribe(value => {
          let mergeResponse = {};
          Object.entries(value).map(([pageIndex, page]) => {
            Object.assign(mergeResponse, page);
          });
          this.responsesChange.emit(mergeResponse);
        }),
        this.mainFormGroup.statusChanges
          // Removed debounced time to solve the form error label display for 200ms before being hidden.
          // .pipe(debounceTime(20))
          .subscribe(status => {
            this.pageValid.emit(status === 'VALID' || status === 'DISABLED');
          })
      ];
      this.pages = this.pending.questionPages.map((questions, index) => {
        /**
         * Add formGroup and value changes subscription for each page
         */
        let pageFormGroup = this.mainFormGroup.controls[
          index
        ] as UntypedFormGroup;

        return {
          index: index,
          questions,
          skipPage: false,
          formGroup: pageFormGroup,
          formSubscriptions: [
            // Triggered when each form control value changes
            pageFormGroup.valueChanges.subscribe(value => {
              if (!this.mainFormGroup.pristine) {
                this.pageUpdated.emit(true);
              }
              questions.forEach(question => {
                if (
                  question.conditionalRules &&
                  question.conditionalRules.length > 0
                ) {
                  /** DISPLAY CONDITION
                   * if it has DISPLAY_CONDITION rule check this rule valids or not
                   * Make checkConditionalQuestions (Only send question and value
                   * make dynamic conditional parser so that no need to send doCompulsory or doLimitCondition spearately)
                   */
                  let normalizedConditions =
                    this.parser.evaluateQuestionExpression(question, value);

                  if (normalizedConditions) {
                    normalizedConditions.map(rule => {
                      switch (rule.type) {
                        case 'DISPLAY_CONDITION':
                          question.display = rule.result;
                          if (
                            rule.result == false &&
                            pageFormGroup.controls[question.key]
                          ) {
                            pageFormGroup.controls[question.key].markAsPristine(
                              { onlySelf: true }
                            );
                            pageFormGroup.controls[
                              question.key
                            ].markAsUntouched({ onlySelf: true });
                            pageFormGroup.controls[
                              question.key
                            ].updateValueAndValidity({ onlySelf: true });
                          }
                          break;

                        case 'COMPULSORY_CONDITION':
                          // Add/Remove required validator and conditional flag inside req constraint based on rule.result
                          // Conditional flag is used in Question Title component to distinguish between conditional and normal question
                          // Also it requires during dynamic error message
                          const findReqIndex = question.constraints.findIndex(
                            c => c.type === ConstraintTypesV3.REQUIRED
                          );
                          if (rule.result == true && question.display) {
                            question.constraints[findReqIndex]
                              ? (question.constraints[
                                  findReqIndex
                                ].conditional = true)
                              : void 0;
                            question.controlType ===
                              QuestionV3ControlTypes.CHECKBOX ||
                            question.controlType ===
                              QuestionV3ControlTypes.TOGGLE ||
                            question.controlType ===
                              QuestionV3ControlTypes.DROPDOWN
                              ? pageFormGroup.controls[
                                  question.key
                                ].addValidators([RequiredGroupValidator()])
                              : pageFormGroup.controls[
                                  question.key
                                ].addValidators([Validators.required]);
                          } else {
                            question.constraints[findReqIndex]
                              ? (question.constraints[
                                  findReqIndex
                                ].conditional = false)
                              : void 0;
                            question.controlType ===
                              QuestionV3ControlTypes.CHECKBOX ||
                            question.controlType ===
                              QuestionV3ControlTypes.TOGGLE ||
                            question.controlType ===
                              QuestionV3ControlTypes.DROPDOWN
                              ? pageFormGroup.controls[
                                  question.key
                                ].clearValidators()
                              : pageFormGroup.controls[
                                  question.key
                                ].removeValidators([Validators.required]);
                          }
                          pageFormGroup.controls[
                            question.key
                          ].updateValueAndValidity({
                            onlySelf: true
                          });
                          break;

                        case 'LIMIT_CONDITION':
                          /**
                           * * Limit condition should handle for visible and required/compulsory question
                           * If question is required check value exist or not
                           * Do not display error if question is optional and has no value
                           * */
                          const isRequired = question.constraints.find(
                            c => c.type === ConstraintTypesV3.REQUIRED
                          );
                          const findLimitIndex = question.constraints.findIndex(
                            c => c.type === ConstraintTypesV3.LIMIT_CONDITION
                          );

                          if (
                            rule.result == false &&
                            question.display &&
                            (isRequired ||
                              isRequired?.conditional ||
                              pageFormGroup.controls[question.key].value)
                          ) {
                            question.constraints[findLimitIndex]
                              ? (question.constraints[
                                  findLimitIndex
                                ].conditional = true)
                              : void 0;
                            pageFormGroup = this.syncWithCustomValidators(
                              question,
                              pageFormGroup
                            );
                            pageFormGroup.controls[question.key].addValidators(
                              LimitCondition()
                            );
                          } else {
                            question.constraints[findLimitIndex]
                              ? (question.constraints[
                                  findLimitIndex
                                ].conditional = false)
                              : void 0;
                            pageFormGroup.controls[
                              question.key
                            ].clearValidators();
                            pageFormGroup = this.syncWithCustomValidators(
                              question,
                              pageFormGroup
                            );
                          }
                          pageFormGroup.controls[
                            question.key
                          ].updateValueAndValidity({
                            onlySelf: true
                          });
                          break;

                        default:
                          return;
                      }
                    });
                  }
                }
              });
            })
          ]
        };
      });

      this.updateProgress();
      this.pending.questionPages = void 0;
    }

    /**
     * SET display condition / add & remove validator here
     */
    if (this.pending.responses !== void 0 && this.pages) {
      this.mainFormGroup.patchValue(this.pending.responses, {
        emitEvent: false
      });
      // pageArray Variable to store page number and its display status
      const pageArray: Array<any> = [];
      this.pages.forEach(page => {
        // pageHasDisplay counter to increase if any input question contains display:true
        let pageHasDisplay = 0;
        page.questions.forEach(question => {
          if (
            question.conditionalRules &&
            question.conditionalRules.length > 0
          ) {
            /** DISPLAY CONDITION
             * if it has DISPLAY_CONDITION rule check this rule valids or not
             */
            let normalizedConditions = this.parser.evaluateQuestionExpression(
              question,
              this.pending.responses
            );

            if (normalizedConditions) {
              normalizedConditions.map(rule => {
                switch (rule.type) {
                  case 'DISPLAY_CONDITION':
                    question.display = rule.result;
                    // If the question has display:false reset value
                    if (question.display == false || rule.result == false) {
                      Array.isArray(this.pending.responses[question.key])
                        ? (this.pending.responses[question.key] = [])
                        : (this.pending.responses[question.key] = void 0);
                    }
                    break;

                  case 'COMPULSORY_CONDITION':
                    // Add/Remove required validator and conditional flag inside req constraint based on rule.result
                    const findReqIndex = question.constraints.findIndex(
                      c => c.type === ConstraintTypesV3.REQUIRED
                    );
                    if (rule.result == true && question.display) {
                      question.constraints[findReqIndex]
                        ? (question.constraints[findReqIndex].conditional =
                            true)
                        : void 0;
                      question.controlType ===
                        QuestionV3ControlTypes.CHECKBOX ||
                      question.controlType === QuestionV3ControlTypes.TOGGLE ||
                      question.controlType === QuestionV3ControlTypes.DROPDOWN
                        ? page.formGroup.controls[question.key].addValidators([
                            RequiredGroupValidator()
                          ])
                        : page.formGroup.controls[question.key].addValidators([
                            Validators.required
                          ]);
                    } else {
                      question.constraints[findReqIndex]
                        ? (question.constraints[findReqIndex].conditional =
                            false)
                        : void 0;
                      question.controlType ===
                        QuestionV3ControlTypes.CHECKBOX ||
                      question.controlType === QuestionV3ControlTypes.TOGGLE ||
                      question.controlType === QuestionV3ControlTypes.DROPDOWN
                        ? page.formGroup.controls[
                            question.key
                          ].clearValidators()
                        : page.formGroup.controls[
                            question.key
                          ].removeValidators([Validators.required]);
                      page.formGroup.controls[
                        question.key
                      ].updateValueAndValidity({
                        onlySelf: true
                      });
                    }
                    break;

                  case 'LIMIT_CONDITION':
                    /**
                     * * Limit condition should handle for visible and required/compulsory question
                     * If question is required check value exist or not
                     * Do not display error if question is optional and has no value
                     * */
                    const isRequired = question.constraints.find(
                      c => c.type === ConstraintTypesV3.REQUIRED
                    );
                    const findLimitIndex = question.constraints.findIndex(
                      c => c.type === ConstraintTypesV3.LIMIT_CONDITION
                    );
                    if (
                      rule.result == false &&
                      question.display &&
                      (isRequired ||
                        isRequired?.conditional ||
                        page.formGroup.controls[question.key].value)
                    ) {
                      question.constraints[findLimitIndex]
                        ? (question.constraints[findLimitIndex].conditional =
                            true)
                        : void 0;
                      page.formGroup = this.syncWithCustomValidators(
                        question,
                        page.formGroup
                      );
                      if (page.formGroup.controls[question.key].value) {
                        page.formGroup.controls[question.key].addValidators(
                          LimitCondition()
                        );
                      }
                    } else {
                      page.formGroup.controls[question.key].clearValidators();
                      page.formGroup = this.syncWithCustomValidators(
                        question,
                        page.formGroup
                      );
                    }
                    page.formGroup.controls[
                      question.key
                    ].updateValueAndValidity({
                      onlySelf: true
                    });
                    break;

                  default:
                    return;
                }
              });
            }
          }

          /**
           * Check if current question is not the headline and contains display:true
           */
          if (!question.isHeadline && question.display) {
            pageHasDisplay++;
          }
        });
        // To skip page if page index has true
        // create array of boolean, [ 0: false, 1: true, 2: false] each index refer to page number, boolen value refer to skip(true) or not(false)
        pageArray.push(pageHasDisplay < 1 ? true : false);

        /**
         * Used to patch form value Initially and aftechanges
         */
        page.formGroup.patchValue(this.pending.responses, {
          emitEvent: false
        });
      });

      /**
       * Trigger CurrentPageStatus action to store the display status of all pages.
       * PageHasDisplay is set True if atleast one input in a page has display:true,
       * PageHasDisplay is set False if all the input in a page has display:false,
       * Used to show/hide page based on conditional field.
       */
      if (pageArray.length > 0 && this.pages) {
        this.skipPageChange.emit(pageArray);
        setTimeout(() => {
          this.updateProgress();
        }, 100);
      }

      this.pageValid.emit(
        this.pages.find(page => page.index === this.currentPage).formGroup.valid
      );

      this.pending.responses = void 0;
    }

    if (this.pending.currentPage !== void 0 && this.pages) {
      this._currentPage = this.pending.currentPage;

      this.pageUpdated.emit(false);
      this.pageValid.emit(
        this.pages.find(page => page.index === this.currentPage)
          ? this.pages.find(page => page.index === this.currentPage).formGroup
              .valid
          : false
      );
      this.updateProgress();
      this.pending.currentPage = void 0;
    }

    /**
     * Emit invalidFields to myForm to scroll to invalid field
     */
    if (this.pending.invalidFields !== void 0 && this.pages) {
      if (this.pending.invalidFields.length) {
        this.invalidFieldIDS.emit(
          this.pending.invalidFields.map(
            field => `question-${field.questionKey}`
          )
        );

        const currentPageData = this.pages.find(
          page => page.index === this.currentPage
        );

        this.pending.invalidFields.forEach(field => {
          if (currentPageData) {
            currentPageData.formGroup.controls[
              field.questionKey
            ].markAsTouched();
            currentPageData.formGroup.controls[field.questionKey].markAsDirty();
          }
        });
      }

      this.pending.invalidFields = void 0;
    }

    if (this.pending.dataToCheckFields !== void 0 && this.pages) {
      this.pages.forEach(page =>
        page.questions.forEach(question =>
          question.checkIfIsDataToCheck(this.pending.dataToCheckFields)
        )
      );

      this.pending.dataToCheckFields = void 0;
    }

    /**
     * When user touch/updates/click next/save form
     * check current page is valid or not
     * Set current page status in store
     * */
    if (this.pages) {
      this.pageValidation = this.parser.checkPageValidation(
        this.pages,
        this.currentPage
      );

      /***
       * Invalidfields is used to store invalid input question key for current page
       * to scroll to invalid field position on page submit
       */
      const invalidFields: FormV3InvalidField[] = [];
      this.pages.map(page => {
        Object.keys(page.formGroup.controls).map(id => {
          if (
            page.index === this.currentPage &&
            page.formGroup.controls[id].status === 'INVALID'
          ) {
            invalidFields.push({
              questionKey: id
            });
          }
        });
      });

      /**
       * Set the curent page status in store
       */
      this.store$.dispatch(
        new CoreActionSetFormCurrentPageStatus({
          status: this.pageValidation,
          invalidFields: invalidFields
        })
      );
    }
  } // Process Pending ends

  private pageFormUnsubscribe(): void {
    if (this.pages) {
      this.pages.forEach(page =>
        page.formSubscriptions.forEach(subscription =>
          subscription.unsubscribe()
        )
      );
    }

    if (this.mainFormSubscriptions) {
      this.mainFormSubscriptions.forEach(subscription =>
        subscription.unsubscribe()
      );
    }
  }

  private updateProgress(): void {
    this._progress = [this.currentPage, (this.pages && this.pages.length) || 0];
    this.progressChange.emit(this._progress);
  }

  private createFormGroup(questions: QuestionBaseV3[]): UntypedFormGroup {
    const form = questions.reduce((prev, question) => {
      switch (question.controlType) {
        case QuestionV3ControlTypes.CHECKBOX:
        case QuestionV3ControlTypes.TOGGLE:
          const isGroupRequired = question.constraints
            .map(constraint => constraint.type === 'required')
            .includes(true);

          const ctFormControls = question['options'].map(() => {
            return new UntypedFormControl('');
          });
          return {
            ...prev,
            [question['key']]: this.formBuilder.array(
              ctFormControls,
              question.validators
            )
          };

        case QuestionV3ControlTypes.DROPDOWN:
          const isRequired = question.constraints
            .map(constraint => constraint.type === 'required')
            .includes(true);

          const dropDownFormControls = question['options'].map(() => {
            return new UntypedFormControl('');
          });

          return {
            ...prev,
            [question['key']]: [dropDownFormControls, question.validators]
          };

        // TODO:reimplement this
        // case QuestionV3ControlTypes.NESTED_GROUP:
        //   const formControls: FormControl[] = question['nestedQuestions'].map(
        //     q => {
        //       return this.formBuilder.control('');
        //     }
        //   );
        //   prev[question['key']] = this.formBuilder.array(formControls);
        //   return prev;

        // case QuestionV3ControlTypes.FIELDS_BLOC:
        //   (question as FieldsBlocQuestionV3).repetitions.map(repetition =>
        //     repetition.map(field => {
        //       prev[field['key']] = this.createFormControl(field);
        //     })
        //   );
        //   return prev;

        default:
          prev[question['key']] = new UntypedFormControl(
            '',
            question.validators
          );
          return prev;
      }
    }, {});

    return this.formBuilder.group(form);
  }

  private syncWithCustomValidators(
    question,
    formGroup: UntypedFormGroup
  ): UntypedFormGroup {
    if (
      question.display &&
      question.constraints.find(c => c.type === ConstraintTypesV3.REQUIRED)
    ) {
      formGroup.controls[question.key].setValidators(
        formGroup.controls[question.key].validator
          ? question.controlType === QuestionV3ControlTypes.CHECKBOX ||
            question.controlType === QuestionV3ControlTypes.TOGGLE ||
            question.controlType === QuestionV3ControlTypes.DROPDOWN
            ? [
                formGroup.controls[question.key].validator,
                RequiredGroupValidator()
              ]
            : [formGroup.controls[question.key].validator, Validators.required]
          : question.controlType === QuestionV3ControlTypes.CHECKBOX ||
            question.controlType === QuestionV3ControlTypes.TOGGLE ||
            question.controlType === QuestionV3ControlTypes.DROPDOWN
          ? [RequiredGroupValidator()]
          : [Validators.required]
      );
    }
    if (
      question.display &&
      question.constraints.find(c => c.type === ConstraintTypesV3.MAXLENGTH)
    ) {
      const maxLen = question.constraints.find(
        c => c.type === ConstraintTypesV3.MAXLENGTH
      );
      formGroup.controls[question.key].setValidators(
        formGroup.controls[question.key].validator
          ? [
              formGroup.controls[question.key].validator,
              Validators.maxLength(maxLen.max)
            ]
          : [Validators.maxLength(maxLen.max)]
      );
    }
    if (
      question.display &&
      question.constraints.find(c => c.type === ConstraintTypesV3.MINLENGTH)
    ) {
      const minLen = question.constraints.find(
        c => c.type === ConstraintTypesV3.MINLENGTH
      );
      formGroup.controls[question.key].setValidators(
        formGroup.controls[question.key].validator
          ? [
              formGroup.controls[question.key].validator,
              Validators.maxLength(minLen.min)
            ]
          : [Validators.maxLength(minLen.min)]
      );
    }
    if (
      question.display &&
      question.constraints.find(c => c.type === ConstraintTypesV3.MAX)
    ) {
      const maxConst = question.constraints.find(
        c => c.type === ConstraintTypesV3.MAX
      );
      formGroup.controls[question.key].setValidators(
        formGroup.controls[question.key].validator
          ? [
              formGroup.controls[question.key].validator,
              Validators.max(maxConst.value)
            ]
          : [Validators.max(maxConst.value)]
      );
    }
    if (
      question.display &&
      question.constraints.find(c => c.type === ConstraintTypesV3.MIN)
    ) {
      const minConst = question.constraints.find(
        c => c.type === ConstraintTypesV3.MIN
      );
      formGroup.controls[question.key].setValidators(
        formGroup.controls[question.key].validator
          ? [
              formGroup.controls[question.key].validator,
              Validators.min(minConst.value)
            ]
          : [Validators.min(minConst.value)]
      );
    }
    if (
      question.display &&
      question.constraints.find(c => c.type === ConstraintTypesV3.EMAIL)
    ) {
      const emailConst = question.constraints.find(
        c => c.type === ConstraintTypesV3.EMAIL
      );
      formGroup.controls[question.key].setValidators(
        formGroup.controls[question.key].validator
          ? [formGroup.controls[question.key].validator, emailValidator()]
          : [emailValidator()]
      );
    }
    if (
      question.display &&
      question.constraints.find(c => c.type === ConstraintTypesV3.MAXSIZE)
    ) {
      const maxSizeConst = question.constraints.find(
        c => c.type === ConstraintTypesV3.MAXSIZE
      );
      formGroup.controls[question.key].setValidators(
        formGroup.controls[question.key].validator
          ? [
              formGroup.controls[question.key].validator,
              maxSizeValidator(maxSizeConst.maxSize)
            ]
          : [maxSizeValidator(maxSizeConst.maxSize)]
      );
    }
    if (
      question.display &&
      question.constraints.find(
        c => c.type === ConstraintTypesV3.ALLOWED_EXTENSION
      )
    ) {
      const allowedExtensionConst = question.constraints.find(
        c => c.type === ConstraintTypesV3.ALLOWED_EXTENSION
      );
      formGroup.controls[question.key].setValidators(
        formGroup.controls[question.key].validator
          ? [
              formGroup.controls[question.key].validator,
              allowedExtensionValidator(allowedExtensionConst.allowedExtension)
            ]
          : [allowedExtensionValidator(allowedExtensionConst.allowedExtension)]
      );
    }
    if (
      question.display &&
      question.constraints.find(c => c.type === ConstraintTypesV3.LENGTH)
    ) {
      const lenConst = question.constraints.find(
        c => c.type === ConstraintTypesV3.LENGTH
      );
      formGroup.controls[question.key].setValidators(
        formGroup.controls[question.key].validator
          ? [
              formGroup.controls[question.key].validator,
              lengthValidator(lenConst.length, true)
            ]
          : [lengthValidator(lenConst.length, true)]
      );
    }
    if (
      question.display &&
      question.constraints.find(c => c.type === ConstraintTypesV3.RANGE)
    ) {
      const rangeConst = question.constraints.find(
        c => c.type === ConstraintTypesV3.RANGE
      );
      formGroup.controls[question.key].setValidators(
        formGroup.controls[question.key].validator
          ? [
              formGroup.controls[question.key].validator,
              createRangeValidator(rangeConst.min, rangeConst.max)
            ]
          : createRangeValidator(rangeConst.min, rangeConst.max)
      );
    }
    formGroup.controls[question.key].updateValueAndValidity({
      onlySelf: true
    });
    return formGroup;
  }
}
